import React from 'react';

import { useMutation } from '@apollo/client/react';
import { c } from 'ttag';

import { Alert, Button, Modal, ModalContent, ModalFooter } from '@freelancelabs/components';

import RefusalNotice from '../../components/RefusalNotice';
import { MembershipStatus } from '../../graphql/__generated__/schema';
import { PortalUserDocument, ReinstateMembershipDocument } from '../../graphql/__generated__/types';
import { reinstateRefusalOf } from './reinstateRefusal';
import { ClosureState, closureAuthorSentence, closureReasonSentence } from './revocation';

interface Props {
    membershipId: string;
    organizationName: string;
    roleName: string;
    /** The closure being undone, already read against `status` by the sheet. */
    closure: ClosureState;
    onClose: () => void;
    /**
     * Picking up the second half of the gesture. Reopening sends no mail, so a person
     * who never activated comes back « invitée » with nothing to activate: the sheet
     * chains on the resend rather than leaving the operator to notice.
     */
    onResendInvitation: () => void;
}

/**
 * The confirmation of « rouvrir l'accès ».
 *
 * **Why a dialog rather than a bare button.** Reopening undoes a decision somebody
 * took, and the reason they took it is the one thing that must be read **before** the
 * click, not after: an operator reopening blind restores an access that was closed
 * for a cause that may still hold. The motive is therefore the body of this dialog,
 * not a footnote of the sheet.
 *
 * **Attention, the gesture is not finished when the mutation succeeds.** Reopening sends no
 * email and creates no invitation, and the revocation had cancelled the one that was
 * pending when it closed the person's last access. The returned status is what says
 * which of the two endings this is: `ACTIVE` stands on its own, the person signs in
 * as before: while `INVITED` means the access exists and nothing can reach it. The
 * success screen of that second case is not a confirmation, it is the next step.
 */
const ReinstateMembershipDialog = ({
    membershipId,
    organizationName,
    roleName,
    closure,
    onClose,
    onResendInvitation,
}: Props) => {
    const [reinstate, { data, loading, error }] = useMutation(ReinstateMembershipDocument, {
        // The sheet prints the status of every membership and the state of the
        // invitation; both change here. Without the refetch the operator would read
        // the state they just left behind, and the resend they are about to chain
        // would be judged on it.
        refetchQueries: [PortalUserDocument],
        awaitRefetchQueries: true,
    });

    const result = data?.reinstateMembership;

    const submit = () => {
        // The refusal is read off `error` below; without this the rejected promise
        // reaches the console as an unhandled one on every refusal.
        reinstate({ variables: { membershipId } }).catch(() => {});
    };

    /* ------------------------------------------------------------------- done */
    if (result) {
        // Attention, `INVITED` is the half-finished ending, and it is the common one: a
        // person who was closed before ever activating comes back with an access and
        // no way in.
        const needsInvitation = result.status === MembershipStatus.Invited;

        return (
            <Modal open onClose={onClose} title={c('Reinstate access').t`Access reopened`}>
                <ModalContent>
                    <Alert
                        type={needsInvitation ? 'warning' : 'success'}
                        title={
                            needsInvitation
                                ? c('Reinstate access').t`The access is open again, but nobody has been told.`
                                : c('Reinstate access').t`${organizationName} is open to this person again.`
                        }
                    >
                        <p className="text-sm">
                            {needsInvitation
                                ? c('Reinstate access')
                                      .t`Reopening sends no email, and the closure had cancelled the invitation that was pending. This person has never activated their account: the access exists and nothing can reach it until a new activation link is sent.`
                                : c('Reinstate access')
                                      .t`They had already activated their account, so they sign in as before and find this account again, with the role and the perimeter they had.`}
                        </p>
                    </Alert>

                    {/* Said on the screen that reports the success, because it is the
                        only place the operator is still looking. The motive does not
                        disappear from the sheet either: a closure stays recorded once
                        it has been undone. */}
                    <p className="mt-4 text-xs text-text-600">
                        {c('Reinstate access')
                            .t`The closure stays on record, with its date and its reason. It now reads as a past event on the sheet, not as the state of the access.`}
                    </p>
                </ModalContent>
                <ModalFooter>
                    {needsInvitation ? (
                        <>
                            <Button category="weak" onClick={onClose}>
                                {c('Action').t`Close`}
                            </Button>
                            <Button category="default" onClick={onResendInvitation}>
                                {c('Reinstate access').t`Send the invitation`}
                            </Button>
                        </>
                    ) : (
                        <Button category="default" onClick={onClose}>
                            {c('Action').t`Close`}
                        </Button>
                    )}
                </ModalFooter>
            </Modal>
        );
    }

    /* ---------------------------------------------------------------- confirm */
    const authorSentence = closureAuthorSentence(closure);
    const reasonSentence = closureReasonSentence(closure);

    return (
        <Modal open onClose={onClose} title={c('Reinstate access').t`Reopen this access`}>
            <ModalContent>
                {/* THE REASON THIS DIALOG EXISTS: the motive of the closure, read
                    before the click. A reopening decided without it undoes somebody
                    else's decision without knowing what it was. */}
                {authorSentence && (
                    <Alert type="warning" title={authorSentence}>
                        <p className="text-sm">{reasonSentence}</p>
                    </Alert>
                )}

                <p className="mt-4 text-xs text-text-600">
                    {c('Reinstate access')
                        .t`Reopening restores the access to ${organizationName} exactly as it was: the role « ${roleName} » and the perimeter that went with it. The gesture takes no role of its own.`}
                </p>
                <p className="mt-2 text-xs text-text-600">
                    {c('Reinstate access')
                        .t`No email leaves: if this person never activated their account, sending them an invitation is a second gesture, offered right after.`}
                </p>

                {error && (
                    <div className="mt-4">
                        <RefusalNotice refusal={reinstateRefusalOf(error)} />
                    </div>
                )}
            </ModalContent>
            <ModalFooter>
                <Button category="weak" onClick={onClose} disabled={loading}>
                    {c('Action').t`Cancel`}
                </Button>
                <Button category="default" onClick={submit} loading={loading}>
                    {c('Reinstate access').t`Reopen`}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default ReinstateMembershipDialog;
